// @flow
import { put, takeEvery, takeLatest, call } from 'redux-saga/effects';

import { getLogger } from 'utils/logger';
import { mkQcsSyncResourceConfig, mkPollConfig } from 'data/qcsSyncResourceTypes';
import { qcsSyncCreateResource, qcsSyncDeleteResource, qcsSyncUpdateResource, getQcsSyncResources, qcsSyncPollResource } from 'data/qcsSyncResourceSagaHelpers';
import * as categoryActions from 'data/categories/actions';
import * as tagActions from 'data/tags/actions';

import * as actions from './actions';
import { getLastSyncDate } from './selectors';
import { transformResponseToDatasets } from './transformers';

const log = getLogger('data/datasets/sagas');

const mkResourceConfig = (successAction, failureAction) => mkQcsSyncResourceConfig({
  resourceName: 'datasets',
  resourceBaseUrl: '/datasets',
  axiosConfig: { headers: { 'qcs-dataset-id': null } },
  getLastSyncDate,
  transformResponseToResources: transformResponseToDatasets,
  successAction,
  failureAction,
});

// =================================================================================================
// Get Datasets
// =================================================================================================
export function* getDatasetsActionWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.getDatasets, getQcsSyncResources, mkResourceConfig(actions.getDatasetsSuccess, actions.getDatasetsFailure));
}

// create
// =================================================================================================
export function* createDatasetActionWatcher(): Generator<*, *, *> {
  yield takeEvery(actions.createDataset, qcsSyncCreateResource, mkResourceConfig(actions.createDatasetSuccess, actions.createDatasetFailure));
}

function* createDatasetFailure() {
  log.log('createDatasetFailureAction...');
  yield put(actions.getDatasets());
}

function* createDatasetFailureActionWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.createDatasetFailure, createDatasetFailure);
}

// delete
// =================================================================================================
export function* deleteDatasetActionWatcher(): Generator<*, *, *> {
  yield takeEvery(actions.deleteDataset, qcsSyncDeleteResource, mkResourceConfig(actions.deleteDatasetSuccess, actions.deleteDatasetFailure));
}

function* deleteDatasetFailure() {
  log.log('deleteDatasetFailureAction...');
  yield put(actions.getDatasets());
}

function* deleteDatasetFailureActionWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.deleteDatasetFailure, deleteDatasetFailure);
}

// update
// =================================================================================================
export function* updateDatasetActionWatcher(): Generator<*, *, *> {
  yield takeEvery(actions.updateDataset, qcsSyncUpdateResource, mkResourceConfig(actions.updateDatasetSuccess, actions.updateDatasetFailure));
}

function* updateDatasetFailure() {
  log.log('updateDatasetFailureAction...');
  yield put(actions.getDatasets());
}

function* updateDatasetFailureActionWatcher(): Generator<*, *, *> {
  yield takeLatest(actions.updateDatasetFailure, updateDatasetFailure);
}

function* datasetPollActionWatcher() {
  yield takeEvery(actions.datasetPoll, datasetPoll);
}
function* datasetPoll(action) {
  if (action.payload && action.payload.id) {
    const config = mkQcsSyncResourceConfig({
      resourceName: 'datasets',
      resourceBaseUrl: `/analysis/saga/${action.payload.id}`,
      successAction: actions.datasetPollSuccess,
      failureAction: actions.datasetPollFailure,
      axiosConfig: { headers: { 'qcs-dataset-id': null } },
      pollConfig: mkPollConfig({
        isComplete: (response, _attempt) => (((response || {}).data || {}).result || {}).completedAt,
        pause: 1000,
        attempts: 10, // 1000 * 10 = 10sec
      }),
    });
    yield call(qcsSyncPollResource, config, action);
  }
}

function* datasetPollSuccessActionWatcher() {
  yield takeEvery(actions.datasetPollSuccess, function* getCategories() {yield put(categoryActions.getCategories())});
  yield takeEvery(actions.datasetPollSuccess, function* getTags() {yield put(tagActions.getTags())});
}

// for loading watcher sagas
export default [
  getDatasetsActionWatcher,
  deleteDatasetActionWatcher,
  deleteDatasetFailureActionWatcher,
  createDatasetActionWatcher,
  createDatasetFailureActionWatcher,
  updateDatasetActionWatcher,
  updateDatasetFailureActionWatcher,
  datasetPollActionWatcher,
  datasetPollSuccessActionWatcher,
];
